import { useEffect, useState } from 'react';
import { getEnvironmentInfo } from '../services/trainingApiService/methods/environmentMethods';
import { EnvironmentInfo } from '../services/trainingApiService/types';

interface UseEnvironmentInfoResult {
  data: EnvironmentInfo | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | undefined;
}

let cached: EnvironmentInfo | undefined;
let pending: Promise<EnvironmentInfo> | undefined;

function fetchOnce(): Promise<EnvironmentInfo> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = getEnvironmentInfo()
      .then((v) => {
        cached = v;
        return v;
      })
      .finally(() => {
        pending = undefined;
      });
  }
  return pending;
}

export function useEnvironmentInfo(): UseEnvironmentInfoResult {
  const [data, setData] = useState<EnvironmentInfo | undefined>(cached);
  const [isLoading, setIsLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (cached) {
      setData(cached);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    fetchOnce()
      .then((v) => {
        if (!cancelled) {
          setData(v);
          setIsLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    data,
    isLoading,
    isError: error !== undefined,
    error,
  };
}
